import { useNavigate } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import IntroSlide from "@/components/intro/IntroSlide";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Autoplay from "embla-carousel-autoplay";
import Logo from "@/components/auth/Logo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { checkIsMonitor } from "@/hooks/useMonitorRedirect";

const slides = [
  {
    title: "Bienvenido a Clubbers",
    description: "Tu app de entrenamiento personal, diseñada para ayudarte a alcanzar tus objetivos fitness",
    backgroundImage: "/lovable-uploads/111f8c26-b07b-4d58-9747-4957d28310e4.png"
  },
  {
    title: "Clases Personalizadas",
    description: "Accede a una amplia variedad de clases y reserva tu lugar con anticipación",
    backgroundImage: "/lovable-uploads/676e084b-a179-4f4a-befe-fd3a0bcd4aea.png"
  },
  {
    title: "Seguimiento de Pagos",
    description: "Gestiona tus pagos y mantén un registro de tu membresía de manera sencilla",
    backgroundImage: "/lovable-uploads/102e1d77-6a0f-45c8-b5fb-6d96338494ab.png"
  },
  {
    title: "¡Comienza Ahora!",
    description: "Únete a nuestra comunidad y transforma tu vida con Clubbers",
    backgroundImage: "/lovable-uploads/7889d0ba-9cd3-4b24-932e-7925d6a03b45.png"
  }
];

const Intro = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Helper function to redirect based on role
  const redirectBasedOnRole = async (userId: string) => {
    const isMonitor = await checkIsMonitor(userId);
    if (isMonitor) {
      console.log("🎯 Intro - user is monitor, redirecting to /monitor");
      navigate("/monitor", { replace: true });
    } else {
      console.log("🏠 Intro - user is not monitor, redirecting to /");
      navigate("/", { replace: true });
    }
  };

  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      try {
        console.log("🎬 Intro - checking authentication status...");
        
        // Check if user has already seen intro
        const hasSeenIntro = sessionStorage.getItem("hasSeenIntro") === "true";
        console.log("👁️ Intro - hasSeenIntro:", hasSeenIntro);
        
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        console.log("🔐 Intro - session exists:", !!session);
        
        if (session?.user) {
          // User is authenticated - redirect based on role
          console.log("✅ Intro - authenticated user, checking role for redirect");
          await redirectBasedOnRole(session.user.id);
          return;
        }

        if (hasSeenIntro) {
          // User has seen intro but isn't authenticated - go to auth
          console.log("🔄 Intro - user has seen intro, redirecting to auth");
          navigate("/auth", { replace: true });
          return;
        }

        // User hasn't seen intro and isn't authenticated - show intro
        console.log("📺 Intro - showing intro to new user");
        setIsCheckingAuth(false);
      } catch (error) {
        console.error("❌ Intro - error checking authentication:", error);
        // In case of error, show intro for safety
        setIsCheckingAuth(false);
      }
    };

    checkAuthAndRedirect();
  }, [navigate]);

  const handleGetStarted = async () => {
    console.log("🚀 Intro - user clicked get started");
    
    // Mark intro as seen
    sessionStorage.setItem("hasSeenIntro", "true");
    console.log("✅ Intro - marked intro as seen");
    
    // Check if user already has a session
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      console.log("🔄 Intro - user has session, checking role for redirect");
      await redirectBasedOnRole(session.user.id);
    } else {
      console.log("🔄 Intro - no session, redirecting to auth");
      navigate("/auth", { replace: true });
    }
  };

  const plugin = Autoplay({
    delay: 4000,
    stopOnInteraction: true
  }) as any;

  // Show loading screen while checking authentication
  if (isCheckingAuth) {
    console.log("⏳ Intro - showing loading screen");
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#00C1D6] to-[#2D6970] flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Logo />
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          <p className="text-white/80 text-sm">Verificando estado de autenticación...</p>
        </div>
      </div>
    );
  }

  console.log("🎭 Intro - rendering intro slides");

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-[#00C1D6] to-[#2D6970] overflow-hidden">
      {/* Logo fijo en la parte superior */}
      <div className="absolute top-0 left-0 right-0 z-10 pt-8">
        <Logo />
      </div>

      {/* Carousel en el centro */}
      <div className="h-screen flex items-center">
        <Carousel 
          className="w-full"
          plugins={[plugin]}
          opts={{
            align: "center",
            loop: true,
          }}
          setApi={(api) => {
            api?.on("select", () => {
              setCurrentSlide(api.selectedScrollSnap());
            });
          }}
        >
          <CarouselContent>
            {slides.map((slide, index) => (
              <CarouselItem key={index}>
                <div className={cn(
                  "transition-opacity duration-500",
                  currentSlide === index ? "opacity-100" : "opacity-0"
                )}>
                  <IntroSlide
                    title={slide.title}
                    description={slide.description}
                    backgroundImage={slide.backgroundImage}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

      {/* Indicador de página y botón fijos en la parte inferior */}
      <div className="absolute bottom-0 left-0 right-0 z-10 px-8 pb-32 sm:pb-8 pt-8 flex flex-col-reverse sm:flex-col gap-6">
        <div className="flex justify-center space-x-2">
          {slides.map((_, index) => (
            <div
              key={index}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-300",
                currentSlide === index 
                  ? "bg-white w-4" 
                  : "bg-white/50"
              )}
            />
          ))}
        </div>
        <div className="flex justify-center">
          <Button 
            onClick={handleGetStarted}
            variant="outline"
            className="w-auto px-12 py-6 text-lg bg-black/40 backdrop-blur-md text-white border-white/30 hover:bg-black/60 sm:w-full"
          >
            Comenzar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Intro;
